import React, { useState, useMemo } from 'react';
import {
    X, CheckCircle, XCircle, Building2, MapPin, Calendar, Users, Package, FileCheck, Download, Loader2, AlertCircle, ChevronRight, ChevronDown, Shield, User, HardDrive, Clock, RefreshCw, Eye, Ear, Hand, Image, FileText, Timer, PlusCircle, Pencil, Crown, Briefcase, Users2, Wrench, Check, Square, MessageSquare
} from 'lucide-react';
import { Card } from 'primereact/card';
import {
    ProjectApprovalDetails, AssetApprovalDetail, CheckpointApprovalDetail, EquipmentApprovalDetail, ResourceApprovalDetail
} from '../../../services/projectApprovalService';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import { ResourceTreeView } from './ResourceTreeView';
import { fileService } from '../../../services/fileService';

// Helper Types
interface KpiCardConfig {
    label: string;
    value: number | string;
    icon: any;
    accent: string;
    caption?: string;
    /** Tailwind border-l color class for left accent, e.g. border-l-sky-500 */
    borderLeftClass?: string;
}

interface JourneyStepConfig {
    key: string;
    title: string;
    description: string;
    time?: string;
    tone: string;
}

interface HierarchyPerson {
    id: string;
    name: string;
    email?: string;
    subtitle?: string;
}

interface HierarchyLevel {
    key: string;
    title: string;
    accent: string;
    people: HierarchyPerson[];
}

// Helper Components — compact, industry-standard spacing
export const SectionHeader: React.FC<{
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    accent?: string;
    description?: string;
}> = ({ icon: Icon, label, accent = 'text-slate-500', description }) => (
    <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
            <div className={`flex h-6 w-6 items-center justify-center rounded bg-slate-100 ${accent}`}>
                <Icon className="h-3 w-3" />
            </div>
            <div>
                <p className="text-[11px] font-semibold text-slate-900">{label}</p>
                {description && (
                    <p className="text-[10px] text-slate-500">{description}</p>
                )}
            </div>
        </div>
    </div>
);

export const KpiCard: React.FC<KpiCardConfig> = ({ label, value, icon: Icon, accent, caption, borderLeftClass = 'border-l-slate-300' }) => (
    <div className={`relative overflow-hidden rounded-lg border border-slate-200 border-l-4 bg-white p-3 shadow-sm ${borderLeftClass}`}>
        <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
                {caption && <p className="mt-1 text-xs text-slate-500">{caption}</p>}
            </div>
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${accent} text-white`}>
                <Icon className="h-4 w-4" />
            </div>
        </div>
    </div>
);

export const HierarchyRail: React.FC<{ levels: HierarchyLevel[] }> = ({ levels }) => (
    <div className="flex flex-wrap items-stretch gap-2">
        {levels.map((level, index) => (
            <React.Fragment key={level.key}>
                <HierarchyNode level={level} />
                {index < levels.length - 1 && (
                    <div className="hidden items-center text-slate-300 md:flex">
                        <ChevronRight className="h-4 w-4" />
                    </div>
                )}
            </React.Fragment>
        ))}
    </div>
);

export const HierarchyNode: React.FC<{ level: HierarchyLevel }> = ({ level }) => {
    const count = level.people.length;
    const visiblePeople = level.people.slice(0, 2);
    const remaining = count - visiblePeople.length;

    return (
        <div className="min-w-[100px] flex-1 rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
            <div className={`inline-flex items-center rounded bg-gradient-to-r ${level.accent} px-1.5 py-0.5 text-[10px] font-semibold text-white`}>
                {level.title}
            </div>
            <div className="mt-1.5 text-sm font-bold text-slate-900 tabular-nums">{count || '—'}</div>
            {count === 0 ? (
                <p className="mt-0.5 text-[10px] text-slate-400">Not assigned</p>
            ) : (
                <>
                    <p className="mt-0.5 text-[10px] text-slate-500 truncate">
                        {visiblePeople.map((person) => person.name).join(', ')}
                        {remaining > 0 ? ` +${remaining}` : ''}
                    </p>
                    {visiblePeople[0]?.email && (
                        <p className="mt-0.5 text-[10px] text-slate-400 truncate">{visiblePeople[0].email}</p>
                    )}
                </>
            )}
        </div>
    );
};

export const JourneyStep: React.FC<{ step: JourneyStepConfig }> = ({ step }) => {
    const toneClasses =
        step.tone === 'completed'
            ? 'bg-emerald-100 text-emerald-600'
            : step.tone === 'rejected'
                ? 'bg-red-100 text-red-600'
                : 'bg-amber-100 text-amber-600';

    const ToneIcon = step.tone === 'completed' ? CheckCircle : step.tone === 'rejected' ? XCircle : Clock;

    return (
        <div className="flex items-start gap-1.5">
            <span className={`mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${toneClasses}`}>
                <ToneIcon className="h-2.5 w-2.5" />
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                    <span className="text-[11px] font-semibold text-slate-900 truncate">{step.title}</span>
                    {step.time && <span className="text-[10px] text-slate-500 shrink-0">{step.time}</span>}
                </div>
                <p className="mt-0.5 text-[10px] text-slate-600">{step.description}</p>
            </div>
        </div>
    );
};

const getInitials = (name: string): string =>
    (name || '').split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';

export const ApproverTile: React.FC<{ role: string; person: { name: string; email?: string } }> = ({ role, person }) => (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5 shadow-sm">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-xs font-bold text-white">
            {getInitials(person.name)}
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{role}</p>
            <p className="text-sm font-semibold text-slate-900 truncate">{person.name}</p>
            {person.email && <p className="text-xs text-slate-500 truncate">{person.email}</p>}
        </div>
    </div>
);

export const InfoTile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="rounded-lg border border-slate-100 bg-slate-50/50 px-2 py-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-0.5 text-[11px] text-slate-900 break-words">{value}</p>
    </div>
);

/** Compact location block: single card with icon, name, address */
export const LocationCard: React.FC<{ location?: { name?: string; address?: string; city?: string } }> = ({ location }) => {
    if (!location?.name && !location?.address) {
        return (
            <div className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/50 px-2.5 py-2">
                <MapPin className="h-4 w-4 text-slate-300 shrink-0" />
                <p className="text-[11px] text-slate-500">No location details</p>
            </div>
        );
    }
    return (
        <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <MapPin className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
                {location.name && <p className="text-sm font-semibold text-slate-900">{location.name}</p>}
                {(location.address || location.city) && (
                    <p className="mt-0.5 text-[11px] text-slate-600">{[location.address, location.city].filter(Boolean).join(', ')}</p>
                )}
            </div>
        </div>
    );
};

/** Vertical approval timeline — modern stepper with connecting line */
export const ApprovalTimeline: React.FC<{ steps: JourneyStepConfig[] }> = ({ steps }) => {
    const getNodeStyle = (tone: string) => {
        if (tone === 'completed') return 'bg-emerald-500 border-emerald-500 text-white';
        if (tone === 'rejected') return 'bg-red-500 border-red-500 text-white';
        return 'bg-amber-500 border-amber-500 text-white';
    };
    const ToneIcon = (tone: string) => (tone === 'completed' ? CheckCircle : tone === 'rejected' ? XCircle : Clock);
    return (
        <div className="relative pl-6">
            <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200 rounded-full" />
            {steps.map((step) => {
                const Icon = ToneIcon(step.tone);
                return (
                    <div key={step.key} className="relative flex gap-3 pb-4 last:pb-0">
                        <div className={`absolute left-0 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${getNodeStyle(step.tone)} z-10`}>
                            <Icon className="h-3 w-3" />
                        </div>
                        <div className="flex-1 min-w-0 pt-0.5">
                            <div className="flex flex-wrap items-center justify-between gap-1">
                                <span className="text-[11px] font-semibold text-slate-900">{step.title}</span>
                                {step.time && <span className="text-[10px] text-slate-500 tabular-nums">{step.time}</span>}
                            </div>
                            <p className="mt-0.5 text-[10px] text-slate-600 leading-snug">{step.description}</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/** Horizontal approval journey — steps in a row with colored nodes and arrow connectors */
export const ApprovalTimelineHorizontal: React.FC<{ steps: JourneyStepConfig[] }> = ({ steps }) => {
    const getNodeStyle = (tone: string) => {
        if (tone === 'completed') return 'bg-emerald-500 text-white ring-2 ring-emerald-200';
        if (tone === 'rejected') return 'bg-red-500 text-white ring-2 ring-red-200';
        return 'bg-amber-500 text-white ring-2 ring-amber-200';
    };
    const ToneIcon = (tone: string) => (tone === 'completed' ? CheckCircle : tone === 'rejected' ? XCircle : Clock);
    return (
        <div className="flex flex-wrap items-stretch gap-0">
            {steps.map((step, i) => {
                const Icon = ToneIcon(step.tone);
                return (
                    <React.Fragment key={step.key}>
                        {i > 0 && (
                            <div className="flex shrink-0 items-center px-1" aria-hidden>
                                <ChevronRight className="h-4 w-4 text-slate-300" />
                            </div>
                        )}
                        <div className="flex min-w-0 flex-1 basis-0 flex-col rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                            <div className="flex items-center gap-2">
                                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${getNodeStyle(step.tone)}`}>
                                    <Icon className="h-3.5 w-3.5" />
                                </div>
                                <span className="text-sm font-semibold text-slate-900 truncate">{step.title}</span>
                            </div>
                            <p className="mt-1 text-xs text-slate-600 line-clamp-2">{step.description}</p>
                            {step.time && <p className="mt-1 text-[10px] text-slate-500 tabular-nums">{step.time}</p>}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

/** Resource hierarchy tree: Head → Manager → Supervisors (with nested Engineers) → Independent Engineers */
export const ResourceTree: React.FC<{
    head: HierarchyPerson | null;
    manager: HierarchyPerson | null;
    supervisors: HierarchyPerson[];
    engineersBySupervisor: HierarchyPerson[][];
    independentEngineers: HierarchyPerson[];
}> = ({ head, manager, supervisors, engineersBySupervisor, independentEngineers }) => {
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['head', 'manager', 'supervisors', 'independent']));
    const toggle = (key: string) => setExpanded((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
    const Node: React.FC<{ id: string; label: string; sublabel?: string; level: number; children?: React.ReactNode; count?: number }> = ({ id, label, sublabel, level, children, count }) => {
        const hasChildren = !!(children && (Array.isArray(children) ? children.length > 0 : true));
        const isOpen = expanded.has(id);
        return (
            <div className="rounded-lg border border-slate-100 bg-white overflow-hidden shadow-sm">
                <button
                    type="button"
                    onClick={() => hasChildren && toggle(id)}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-slate-50/80 transition-colors"
                >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-slate-400">
                        {hasChildren ? (isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
                    </span>
                    <User className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                    <span className="text-[11px] font-semibold text-slate-800 truncate flex-1">{label}</span>
                    {count != null && <span className="text-[10px] text-slate-500 tabular-nums">{count}</span>}
                </button>
                {sublabel && <p className="pl-9 pr-2.5 pb-1.5 text-[10px] text-slate-500 truncate">{sublabel}</p>}
                {hasChildren && isOpen && <div className="border-t border-slate-100 pl-4 pb-2 pt-1 space-y-1">{children}</div>}
            </div>
        );
    };
    return (
        <div className="space-y-1.5">
            {head && <Node id="head" label={head.name} sublabel={head.email} level={0} />}
            {manager && <Node id="manager" label={manager.name} sublabel={manager.email} level={0} />}
            <Node id="supervisors" label="Site Supervisors" count={supervisors.length} level={0} children={
                supervisors.length === 0 ? <p className="text-[10px] text-slate-400 py-1">None assigned</p> : (
                    supervisors.map((sup, i) => (
                        <Node key={sup.id} id={`sup-${sup.id}`} label={sup.name} sublabel={sup.email} level={1} count={engineersBySupervisor[i]?.length ?? 0} children={
                            (engineersBySupervisor[i]?.length ?? 0) > 0 ? engineersBySupervisor[i].map((eng) => (
                                <div key={eng.id} className="flex items-center gap-2 pl-2 py-1 rounded border border-slate-100 bg-slate-50/50">
                                    <User className="h-3 w-3 text-slate-400 shrink-0" />
                                    <span className="text-[11px] font-medium text-slate-700">{eng.name}</span>
                                    {eng.email && <span className="text-[10px] text-slate-500 truncate">{eng.email}</span>}
                                </div>
                            )) : null
                        } />
                    ))
                )
            } />
            {independentEngineers.length > 0 ? (
                <Node id="independent" label="Independent Engineers" count={independentEngineers.length} level={0} children={
                    independentEngineers.map((eng) => (
                        <div key={eng.id} className="flex items-center gap-2 pl-2 py-1 rounded border border-slate-100 bg-slate-50/50">
                            <User className="h-3 w-3 text-slate-400 shrink-0" />
                            <span className="text-[11px] font-medium text-slate-700">{eng.name}</span>
                        </div>
                    ))
                } />
            ) : null}
        </div>
    );
};

export const EmptyApprover: React.FC<{ role: string }> = ({ role }) => (
    <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-400 shrink-0">
            <User className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0">
            <p className="text-[10px] font-medium uppercase text-slate-500">{role}</p>
            <p className="text-[11px] italic text-slate-400">Not assigned</p>
        </div>
    </div>
);

export const getStatusBadge = (status?: string) => {
    if (status === 'approved') {
        return <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">Approved</span>;
    }
    if (status === 'rejected') {
        return <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium">Rejected</span>;
    }
    if (status === 'modified') {
        return <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-medium">Modified</span>;
    }
    return <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-medium">Pending</span>;
};

export const ChangeComparison: React.FC<{
    previousRejectionComment: string;
    currentValue?: string;
    type: 'checkpoint' | 'equipment' | 'resource';
    itemName: string;
}> = ({ previousRejectionComment, currentValue, type, itemName }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="mt-1.5 bg-orange-50 border border-orange-200 rounded-lg">
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full px-2.5 py-1.5 flex items-center justify-between text-left hover:bg-orange-100 transition-colors rounded-t-lg"
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    <RefreshCw className="w-3.5 h-3.5 text-orange-600 shrink-0" />
                    <span className="text-[11px] font-semibold text-orange-800 truncate">
                        Previously rejected — view changes
                    </span>
                </div>
                {expanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-orange-600 shrink-0" />
                ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-orange-600 shrink-0" />
                )}
            </button>
            {expanded && (
                <div className="px-2.5 pb-2.5 space-y-2 border-t border-orange-200">
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                        <p className="text-[10px] font-semibold text-red-800 mb-0.5">Previous rejection</p>
                        <p className="text-[11px] text-red-700">{previousRejectionComment}</p>
                    </div>
                    {currentValue && (
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                            <p className="text-[10px] font-semibold text-green-800 mb-0.5">Current value</p>
                            <p className="text-[11px] text-green-700">{currentValue}</p>
                        </div>
                    )}
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                        <p className="text-[10px] font-semibold text-blue-800 mb-0.5">Item</p>
                        <p className="text-[11px] text-blue-700">{itemName}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

// Compact status dot for dense rows
const StatusDot: React.FC<{ status?: string }> = ({ status }) => {
    const map: Record<string, string> = {
        approved: 'bg-emerald-500',
        rejected: 'bg-red-500',
        modified: 'bg-amber-500',
        pending: 'bg-slate-400',
    };
    return <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${map[status || 'pending'] || 'bg-slate-400'}`} title={status} />;
};

// Frequency colors — aligned with checkpoint master
const FREQUENCY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    Daily: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    Weekly: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    Fortnightly: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    Monthly: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
    Quarterly: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
    'Half-Yearly': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    Yearly: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
};
// Same color family for checkpoint card left block + title bar (solid shades)
const FREQUENCY_CARD_COLORS: Record<string, { leftBlock: string; titleBar: string }> = {
    Daily: { leftBlock: 'bg-blue-600', titleBar: 'bg-blue-500' },
    Weekly: { leftBlock: 'bg-green-600', titleBar: 'bg-green-500' },
    Fortnightly: { leftBlock: 'bg-emerald-600', titleBar: 'bg-emerald-500' },
    Monthly: { leftBlock: 'bg-purple-600', titleBar: 'bg-purple-500' },
    Quarterly: { leftBlock: 'bg-orange-600', titleBar: 'bg-orange-500' },
    'Half-Yearly': { leftBlock: 'bg-red-600', titleBar: 'bg-red-500' },
    Yearly: { leftBlock: 'bg-indigo-600', titleBar: 'bg-indigo-500' },
};
const DEFAULT_FREQ_CARD_COLORS = { leftBlock: 'bg-slate-600', titleBar: 'bg-slate-500' };
const FREQUENCY_ORDER = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'] as const;

// Checkpoint display group: for systematic grouping and highlight
export type CheckpointDisplayGroup = 'fromMaster' | 'updatedFromMaster' | 'newAtProject';

export function getCheckpointDisplayGroup(cp: CheckpointApprovalDetail): CheckpointDisplayGroup {
    if (cp.isModifiedFromMaster) return 'updatedFromMaster';
    if (cp.isProjectLevel && (cp.isNewlyAdded === true || !cp.masterCheckpointId)) return 'newAtProject';
    return 'fromMaster';
}

// Highlight styles per group — so user can easily distinguish
const CHECKPOINT_GROUP_STYLES: Record<CheckpointDisplayGroup, { label: string; leftBorder: string; bg: string; badge: string; badgeText: string; sectionBg: string; sectionBorder: string }> = {
    fromMaster: { label: 'From Master', leftBorder: 'border-l border-l-slate-300', bg: 'bg-white', badge: 'bg-slate-100 text-slate-700 border-slate-200', badgeText: 'Master', sectionBg: 'bg-slate-50/50', sectionBorder: 'border-slate-200' },
    updatedFromMaster: { label: 'Updated from Master', leftBorder: 'border-l-4 border-l-amber-500', bg: 'bg-amber-50/70', badge: 'bg-amber-100 text-amber-800 border-amber-300', badgeText: 'Updated', sectionBg: 'bg-amber-50/40', sectionBorder: 'border-amber-200' },
    newAtProject: { label: 'Added project level new', leftBorder: 'border-l-4 border-l-emerald-500', bg: 'bg-emerald-50/70', badge: 'bg-emerald-100 text-emerald-800 border-emerald-300', badgeText: 'New', sectionBg: 'bg-emerald-50/40', sectionBorder: 'border-emerald-200' },
};

const formatCheckpointDate = (dateInput?: string | Date | null): string => {
    if (!dateInput) return '—';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Professional approval action buttons — theme gradient, sharp shadow, colour transition
const APPROVE_BTN_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold text-sm min-w-[88px] px-3 py-2 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2';
// Active (current decision) — solid filled, cursor-default (not pointer-events-none so disabled attribute still shows)
const APPROVE_BTN_ACTIVE   = `${APPROVE_BTN_BASE} bg-emerald-600 text-white shadow-sm shadow-emerald-500/30 ring-2 ring-emerald-400 cursor-default focus:ring-emerald-400`;
const REJECT_BTN_ACTIVE    = `${APPROVE_BTN_BASE} bg-red-600 text-white shadow-sm shadow-red-500/30 ring-2 ring-red-400 cursor-default focus:ring-red-400`;
// Inactive (not current decision) — subtle outline, clickable
const APPROVE_BTN_INACTIVE = `${APPROVE_BTN_BASE} border-2 border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50 hover:border-emerald-400 focus:ring-emerald-400`;
const REJECT_BTN_INACTIVE  = `${APPROVE_BTN_BASE} border-2 border-red-300 text-red-600 bg-white hover:bg-red-50 hover:border-red-400 focus:ring-red-400`;
// Legacy aliases kept for non-decision areas
const APPROVE_BTN_CLASS = APPROVE_BTN_INACTIVE;
const REJECT_BTN_CLASS  = REJECT_BTN_INACTIVE;
// Compact icon-only variant for dense rows
const APPROVE_BTN_COMPACT         = 'inline-flex items-center justify-center w-8 h-7 rounded-lg transition-all duration-150 border-2 border-emerald-300 text-emerald-600 bg-white hover:bg-emerald-50 disabled:opacity-40';
const APPROVE_BTN_COMPACT_ACTIVE  = 'inline-flex items-center justify-center w-8 h-7 rounded-lg transition-all duration-150 bg-emerald-600 text-white ring-2 ring-emerald-400 cursor-default';
const REJECT_BTN_COMPACT          = 'inline-flex items-center justify-center w-8 h-7 rounded-lg transition-all duration-150 border-2 border-red-300 text-red-600 bg-white hover:bg-red-50 disabled:opacity-40';
const REJECT_BTN_COMPACT_ACTIVE   = 'inline-flex items-center justify-center w-8 h-7 rounded-lg transition-all duration-150 bg-red-600 text-white ring-2 ring-red-400 cursor-default';
// Selection toggle — theme border, sharp focus
const SELECTION_BOX_CLASS = (isSelected: boolean) =>
  `flex items-center justify-center w-5 h-5 rounded-md shrink-0 transition-all duration-200 outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-blue-light-500)] ${isSelected ? 'bg-[var(--color-blue-light-600)] text-white shadow-[var(--shadow-theme-xs)] ring-2 ring-[var(--color-blue-light-400)]/40' : 'border-2 border-[var(--color-gray-300)] bg-white hover:border-[var(--color-blue-light-400)] hover:bg-[var(--color-blue-light-50)] text-transparent'}`;

// Checkpoint card — status for icon + label only; card colors from frequency (same family as FREQUENCY_COLORS)
const CHECKPOINT_STATUS_STYLES = {
    approved: { label: 'Done', Icon: Check },
    pending: { label: 'In Progress', Icon: Clock },
    modified: { label: 'In Progress', Icon: MessageSquare },
    rejected: { label: 'Rejected', Icon: XCircle },
} as const;

/** Checkpoint card — infographic layout: left colored tab + right content with clear decision state */
export const CheckpointTimelineCard: React.FC<{
    checkpoint: CheckpointApprovalDetail;
    index: number;
    isFirst: boolean;
    isLast: boolean;
    onAction: (id: string, name: string, action: 'approve' | 'reject') => Promise<void>;
    isSelected?: boolean;
    onToggleSelection?: (id: string) => void;
    isReviewSubmitted?: boolean;
    isResubmitted?: boolean;
    isHead?: boolean;
}> = ({
    checkpoint,
    index,
    isFirst,
    isLast,
    onAction,
    isSelected,
    onToggleSelection,
    isReviewSubmitted = false,
    isResubmitted = false,
    isHead = true,
}) => {
    const status = (checkpoint.approvalStatus === 'approved' || checkpoint.approvalStatus === 'rejected' || checkpoint.approvalStatus === 'modified' || checkpoint.approvalStatus === 'pending')
        ? checkpoint.approvalStatus
        : 'pending';
    const freqColors = FREQUENCY_CARD_COLORS[checkpoint.frequency] ?? DEFAULT_FREQ_CARD_COLORS;
    const isApproved = checkpoint.approvalStatus === 'approved';
    const isRejected = checkpoint.approvalStatus === 'rejected';
    const isActionable = isHead && !isReviewSubmitted;
    const isSelectable = isActionable && (status === 'pending' || status === 'modified' || (isResubmitted && isRejected));

    // Detect decision change: re-approved after rejection (has rejection comment but now approved)
    //                          or re-rejected after approval
    const hasApproverComment = !!(checkpoint.approverComments && checkpoint.approverComments.trim());
    const wasReApproved = isApproved && hasApproverComment;  // approved but has a prior rejection comment
    const wasReRejected = isRejected && hasApproverComment && isResubmitted; // rejected again after resubmission

    const displayGroup = getCheckpointDisplayGroup(checkpoint);
    const isProjectLevelNew = displayGroup === 'newAtProject';
    const isUpdatedFromMaster = displayGroup === 'updatedFromMaster';

    const stepNumber = String(index).padStart(2, '0');
    const frequencyLabel = (checkpoint.frequency || 'CHECKPOINT').toUpperCase();
    const timeLabel = checkpoint.executionTime ?? '—';
    const durationLabel = checkpoint.bufferTimeDays != null
        ? `${checkpoint.bufferTimeDays}d buffer`
        : (checkpoint.frequency || '—');

    // Card border color based on approval status
    const cardBorder = isApproved
        ? 'border-emerald-300 shadow-sm shadow-emerald-100'
        : isRejected
            ? 'border-red-300 shadow-sm shadow-red-100'
            : isSelected
                ? 'border-blue-400 shadow-md shadow-blue-100'
                : 'border-slate-200';

    // Timeline dot color based on status
    const dotBg = isApproved
        ? 'bg-emerald-500'
        : isRejected
            ? 'bg-red-500'
            : status === 'modified'
                ? 'bg-amber-500'
                : 'bg-slate-400';

    const DotIcon = isApproved ? Check : isRejected ? XCircle : status === 'modified' ? MessageSquare : Clock;

    return (
        <div className="relative flex gap-0 group">
            {/* Timeline dot */}
            <div className="shrink-0 pt-3 w-6 flex justify-center z-10">
                <div className={`flex h-7 w-7 items-center justify-center rounded-full text-white ring-4 ring-white transition-colors duration-200 ${dotBg}`}>
                    <DotIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                </div>
            </div>

            {/* Card */}
            <div
                className={`flex-1 min-w-0 ml-4 -mt-1 flex rounded-xl overflow-hidden bg-white border-2 transition-all duration-200 approval-card ${cardBorder} ${isSelectable ? 'cursor-pointer' : ''} ${isSelected ? 'approval-card-selected' : ''}`}
                onClick={(e) => {
                    if (isSelectable && onToggleSelection && !(e.target as HTMLElement).closest('button')) {
                        onToggleSelection(checkpoint._id);
                    }
                }}
                style={{ cursor: isSelectable ? 'pointer' : 'default' }}
            >
                {/* Left accent strip — frequency colour */}
                <div className={`flex flex-col items-center justify-center min-w-[52px] w-[52px] py-2 rounded-l-[9px] text-white shrink-0 ${freqColors.leftBlock}`}>
                    <span className="text-[7px] font-bold uppercase tracking-wider opacity-90 leading-tight text-center px-1">{frequencyLabel}</span>
                    <span className="text-sm font-bold tabular-nums mt-1">{stepNumber}</span>
                    {isProjectLevelNew && (
                        <span className="mt-1 px-1 py-0.5 rounded bg-white/25 text-white text-[7px] font-bold uppercase tracking-wide leading-none">NEW</span>
                    )}
                    {isUpdatedFromMaster && (
                        <span className="mt-1 px-1 py-0.5 rounded bg-white/25 text-white text-[7px] font-bold uppercase tracking-wide leading-none">UPD</span>
                    )}
                </div>

                {/* Main content */}
                <div className="flex-1 min-w-0 flex flex-col border-l border-slate-200">
                    {/* Header: name + status badge + selection */}
                    <div className={`px-3 py-2.5 border-b flex items-center justify-between gap-2 ${isApproved ? 'bg-emerald-50/60 border-emerald-100' : isRejected ? 'bg-red-50/60 border-red-100' : 'bg-slate-50/60 border-slate-100'}`}>
                        <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900 truncate" title={checkpoint.name}>{checkpoint.name}</span>
                            {/* Approval status badge */}
                            {isApproved && (
                                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide border border-emerald-200">
                                    <Check className="w-3 h-3 stroke-[3]" /> Approved
                                </span>
                            )}
                            {isRejected && (
                                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wide border border-red-200">
                                    <XCircle className="w-3 h-3" /> Rejected
                                </span>
                            )}
                            {(status === 'pending' || status === 'modified') && (
                                <span className="shrink-0 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-medium uppercase border border-slate-200">
                                    Pending
                                </span>
                            )}
                            {/* Project-level type badge */}
                            {isProjectLevelNew && (
                                <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[9px] font-bold uppercase border border-emerald-300">
                                    <PlusCircle className="w-2.5 h-2.5" /> Project New
                                </span>
                            )}
                            {isUpdatedFromMaster && (
                                <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold uppercase border border-amber-300">
                                    <Pencil className="w-2.5 h-2.5" /> Updated
                                </span>
                            )}
                        </div>
                        {isSelectable && onToggleSelection && (
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); onToggleSelection(checkpoint._id); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleSelection(checkpoint._id); } }}
                                className={SELECTION_BOX_CLASS(!!isSelected)}
                                title={isSelected ? 'Deselect' : 'Select for bulk action'}
                                aria-pressed={!!isSelected}
                            >
                                <Check className={`w-3 h-3 stroke-[2.5] ${isSelected ? '' : 'opacity-0'}`} />
                            </div>
                        )}
                    </div>

                    {/* Body: requirement & timing */}
                    <div className="px-3 py-2 bg-white">
                        <p className="text-xs text-slate-600 leading-snug">
                            {timeLabel} · {durationLabel}
                            {checkpoint.requirement && checkpoint.requirement.trim() !== '' && (
                                <span className="block mt-1 text-slate-700">{checkpoint.requirement}</span>
                            )}
                        </p>
                    </div>

                    {/* Re-decision highlight — approved after prior rejection, or re-rejected */}
                    {wasReApproved && (
                        <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-200 flex items-center gap-2">
                            <RefreshCw className="w-3 h-3 text-amber-600 shrink-0" />
                            <span className="text-[10px] font-semibold text-amber-800">Re-approved after rejection</span>
                            <span className="text-[9px] text-amber-700 italic truncate max-w-[200px]" title={checkpoint.approverComments}>Prior: "{checkpoint.approverComments}"</span>
                        </div>
                    )}
                    {wasReRejected && (
                        <div className="px-3 py-1.5 bg-orange-50 border-t border-orange-200 flex items-center gap-2">
                            <RefreshCw className="w-3 h-3 text-orange-600 shrink-0" />
                            <span className="text-[10px] font-semibold text-orange-800">Rejected again after resubmission</span>
                        </div>
                    )}

                    {/* Approver comment when rejected (and NOT re-approved) */}
                    {isRejected && hasApproverComment && !wasReRejected && (
                        <div className="px-3 py-1.5 bg-red-50 border-t border-red-200 flex items-start gap-2">
                            <MessageSquare className="w-3 h-3 text-red-500 shrink-0 mt-0.5" />
                            <span className="text-[10px] text-red-800 leading-snug">{checkpoint.approverComments}</span>
                        </div>
                    )}

                    {/* Approval decision bar */}
                    {isActionable && (
                        <div className="px-3 py-2 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Decision</span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); if (!isApproved) onAction(checkpoint._id, checkpoint.name, 'approve'); }}
                                    disabled={isApproved}
                                    className={isApproved ? APPROVE_BTN_ACTIVE : APPROVE_BTN_INACTIVE}
                                    title={isApproved ? 'Already approved' : 'Approve this checkpoint'}
                                >
                                    <CheckCircle className="w-4 h-4 shrink-0" />
                                    <span>{isApproved ? 'Approved ✓' : 'Approve'}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); if (!isRejected) onAction(checkpoint._id, checkpoint.name, 'reject'); }}
                                    disabled={isRejected}
                                    className={isRejected ? REJECT_BTN_ACTIVE : REJECT_BTN_INACTIVE}
                                    title={isRejected ? 'Already rejected' : 'Reject this checkpoint'}
                                >
                                    <XCircle className="w-4 h-4 shrink-0" />
                                    <span>{isRejected ? 'Rejected ✗' : 'Reject'}</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/** Vertical timeline of checkpoint cards — single dashed line connects all dots */
export const CheckpointTimeline: React.FC<{
    checkpoints: CheckpointApprovalDetail[];
    onAction: (id: string, name: string, action: 'approve' | 'reject') => Promise<void>;
    isSelected?: (id: string) => boolean;
    onToggleSelection?: (id: string) => void;
    isReviewSubmitted?: boolean;
    isResubmitted?: boolean;
    isHead?: boolean;
}> = ({
    checkpoints,
    onAction,
    isSelected,
    onToggleSelection,
    isReviewSubmitted = false,
    isResubmitted = false,
    isHead = true,
}) => {
    if (checkpoints.length === 0) return null;
    return (
        <div className="relative pl-0">
            {/* Single dashed line behind all dots — refined */}
            <div className="absolute left-[11px] top-5 bottom-5 w-0 border-l border-dashed border-slate-200 pointer-events-none" aria-hidden />
            <div className="space-y-4">
                {checkpoints.map((cp, i) => (
                    <CheckpointTimelineCard
                        key={cp._id}
                        checkpoint={cp}
                        index={i + 1}
                        isFirst={i === 0}
                        isLast={i === checkpoints.length - 1}
                        onAction={onAction}
                        isSelected={isSelected?.(cp._id)}
                        onToggleSelection={onToggleSelection}
                        isReviewSubmitted={isReviewSubmitted}
                        isResubmitted={isResubmitted}
                        isHead={isHead}
                    />
                ))}
            </div>
        </div>
    );
};

export const CheckpointRow: React.FC<{
    checkpoint: CheckpointApprovalDetail;
    onAction: (id: string, name: string, action: 'approve' | 'reject') => Promise<void>;
    isCompact: boolean;
    isSelected?: boolean;
    onToggleSelection?: (id: string) => void;
    isReviewSubmitted?: boolean;
    isResubmitted?: boolean;
    isDense?: boolean;
    index?: number;
    isHead?: boolean;
}> = ({
    checkpoint,
    onAction,
    isCompact,
    isSelected,
    onToggleSelection,
    isReviewSubmitted = false,
    isResubmitted = false,
    isDense = false,
    index,
    isHead = true,
}) => {
        const isPending = checkpoint.approvalStatus === 'pending';
        const isApproved = checkpoint.approvalStatus === 'approved';
        const isRejected = checkpoint.approvalStatus === 'rejected';
        const isModified = checkpoint.approvalStatus === 'modified';
        const isActionable = isHead && !isReviewSubmitted;
        const isSelectable = isActionable && (isPending || isModified || (isResubmitted && isRejected));
        const wasPreviouslyRejected = isResubmitted && (isPending || isModified || isRejected) && checkpoint.approverComments;
        const displayGroup = getCheckpointDisplayGroup(checkpoint);
        const groupStyle = CHECKPOINT_GROUP_STYLES[displayGroup];
        const isProjectLevelNew = displayGroup === 'newAtProject';
        const isUpdatedFromMaster = displayGroup === 'updatedFromMaster';
        const hasApproverComment = !!(checkpoint.approverComments && checkpoint.approverComments.trim());
        const wasReApproved = isApproved && hasApproverComment;

        // Dense single-row variant
        if (isDense) {
            const freqStyle = FREQUENCY_COLORS[checkpoint.frequency] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
            const accentColor = isApproved ? 'bg-emerald-500' : isRejected ? 'bg-red-500' : isModified ? 'bg-amber-500' : 'bg-slate-400';
            const cardBorder = isSelected
                ? 'border-blue-400 ring-2 ring-blue-300/50'
                : isApproved ? 'border-emerald-300 bg-emerald-50/50'
                : isRejected ? 'border-red-300 bg-red-50/30'
                : wasReApproved ? 'border-amber-300 bg-amber-50/30'
                : `${freqStyle.border} ${isUpdatedFromMaster || isProjectLevelNew ? 'border-dashed' : ''}`;

            return (
                <div
                    className={`relative flex items-center gap-2 min-h-[40px] pl-3 pr-2.5 rounded-xl border-2 shadow-sm transition-all duration-200 overflow-hidden ${cardBorder} ${isActionable ? 'hover:shadow-md' : ''}`}
                    onClick={(e) => {
                        if (isSelectable && onToggleSelection && !(e.target as HTMLElement).closest('button')) {
                            onToggleSelection(checkpoint._id);
                        }
                    }}
                    style={{ cursor: isSelectable ? 'pointer' : 'default' }}
                >
                    {/* Left status accent strip */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${accentColor} rounded-l-[9px]`} aria-hidden />

                    {index != null && (
                        <span className="flex items-center justify-center w-5 h-5 rounded bg-slate-200/80 text-slate-600 text-[10px] font-bold tabular-nums shrink-0 ml-0.5">{index}</span>
                    )}
                    {isSelectable && onToggleSelection && (
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); onToggleSelection(checkpoint._id); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleSelection(checkpoint._id); } }}
                            className={SELECTION_BOX_CLASS(!!isSelected)}
                            title={isSelected ? 'Deselect' : 'Select'}
                            aria-pressed={!!isSelected}
                        >
                            <Check className={`w-3 h-3 stroke-[2.5] ${isSelected ? '' : 'opacity-0'}`} />
                        </div>
                    )}

                    {/* Status icon */}
                    {isApproved && <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                    {isRejected && <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />}
                    {(isPending || isModified) && <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />}

                    <span className="text-xs font-semibold text-slate-800 truncate flex-1 min-w-0 tracking-tight" title={checkpoint.name}>{checkpoint.name}</span>

                    {/* Re-approved indicator */}
                    {wasReApproved && <span title="Re-approved after rejection"><RefreshCw className="w-3 h-3 text-amber-500 shrink-0" /></span>}

                    <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${freqStyle.bg} ${freqStyle.text} ${freqStyle.border}`}>{checkpoint.frequency}</span>

                    {isProjectLevelNew && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300">NEW</span>
                    )}
                    {isUpdatedFromMaster && !isProjectLevelNew && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-300">UPD</span>
                    )}

                    {isActionable && (
                        <div className="flex gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); if (!isApproved) onAction(checkpoint._id, checkpoint.name, 'approve'); }}
                                disabled={isApproved}
                                className={isApproved ? APPROVE_BTN_COMPACT_ACTIVE : APPROVE_BTN_COMPACT}
                                title={isApproved ? 'Approved' : 'Approve'}
                            >
                                <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); if (!isRejected) onAction(checkpoint._id, checkpoint.name, 'reject'); }}
                                disabled={isRejected}
                                className={isRejected ? REJECT_BTN_COMPACT_ACTIVE : REJECT_BTN_COMPACT}
                                title={isRejected ? 'Rejected' : 'Reject'}
                            >
                                <XCircle className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        // Full compact card variant
        const freqStyle = FREQUENCY_COLORS[checkpoint.frequency] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
        const taskTypeLabels: Record<string, { icon: React.ReactNode; label: string }> = {
            Visual: { icon: <Eye className="w-3 h-3" />, label: 'Visual' },
            Hearing: { icon: <Ear className="w-3 h-3" />, label: 'Hearing' },
            Manual: { icon: <Hand className="w-3 h-3" />, label: 'Manual' },
        };
        const hasDetails = (checkpoint.requirement != null && checkpoint.requirement !== '') ||
            (checkpoint.taskType && checkpoint.taskType.length > 0) ||
            (checkpoint.executionPhase != null && checkpoint.executionPhase !== '') ||
            (checkpoint.executionTime != null && checkpoint.executionTime !== '') ||
            checkpoint.bufferTimeDays != null ||
            (checkpoint.priority != null && checkpoint.priority !== '') ||
            (checkpoint.checkpointCode != null && checkpoint.checkpointCode !== '') ||
            checkpoint.isImageRequired === true ||
            (checkpoint.isInputRequired != null && checkpoint.isInputRequired !== 'None');

        const cardBorder = isSelected
            ? 'border-blue-400 ring-1 ring-blue-300/50'
            : isApproved ? 'border-emerald-300'
            : isRejected ? 'border-red-300'
            : wasReApproved ? 'border-amber-300'
            : `${freqStyle.border} ${isUpdatedFromMaster || isProjectLevelNew ? 'border-dashed' : ''}`;

        const accentStrip = isApproved ? 'bg-emerald-500' : isRejected ? 'bg-red-500' : isModified ? 'bg-amber-500' : 'bg-slate-400';

        const metaLabel = 'text-[9px] font-bold text-slate-500 uppercase tracking-wider';
        const metaValue = 'text-[10px] font-medium text-slate-800';

        return (
            <div
                className={`relative overflow-hidden rounded-xl border-2 transition-all duration-200 bg-white ${cardBorder} ${isSelected ? 'approval-card-selected' : ''}`}
                onClick={(e) => {
                    if (isSelectable && onToggleSelection && !(e.target as HTMLElement).closest('button')) {
                        onToggleSelection(checkpoint._id);
                    }
                }}
                style={{ cursor: isSelectable ? 'pointer' : 'default' }}
            >
                {/* Left status accent strip */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${accentStrip} rounded-l-[9px]`} aria-hidden />

                {/* Row 1: Name + tags + status */}
                <div className={`flex items-center gap-2 min-h-[36px] pl-3 pr-2.5 border-b ${isApproved ? 'bg-emerald-50/50 border-emerald-100' : isRejected ? 'bg-red-50/40 border-red-100' : 'bg-slate-50/60 border-slate-100'}`}>
                    {index != null && (
                        <span className="flex items-center justify-center w-5 h-5 rounded bg-slate-200/80 text-slate-600 text-[10px] font-bold tabular-nums shrink-0">{index}</span>
                    )}
                    {isSelectable && onToggleSelection && (
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); onToggleSelection(checkpoint._id); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleSelection(checkpoint._id); } }}
                            className={SELECTION_BOX_CLASS(!!isSelected)}
                            title={isSelected ? 'Deselect' : 'Select'}
                            aria-pressed={!!isSelected}
                        >
                            <Check className={`w-3 h-3 stroke-[2.5] ${isSelected ? '' : 'opacity-0'}`} />
                        </div>
                    )}
                    <span className="text-xs font-semibold text-slate-900 truncate flex-1 min-w-0" title={checkpoint.name}>{checkpoint.name}</span>
                    <span className={`shrink-0 px-1 py-0.5 rounded text-[9px] font-bold uppercase border ${freqStyle.bg} ${freqStyle.text} ${freqStyle.border}`}>{checkpoint.frequency}</span>
                    {/* Approval status pill */}
                    {isApproved && <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold border border-emerald-200"><Check className="w-2.5 h-2.5 stroke-[3]" />OK</span>}
                    {isRejected && <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[9px] font-bold border border-red-200"><XCircle className="w-2.5 h-2.5" />Rej</span>}
                    {wasReApproved && <span title="Re-approved after rejection"><RefreshCw className="w-3 h-3 text-amber-500 shrink-0" /></span>}
                    {/* Project-level badges */}
                    {isProjectLevelNew && <span className="shrink-0 px-1 py-0.5 rounded text-[8px] font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300">NEW</span>}
                    {isUpdatedFromMaster && !isProjectLevelNew && <span className="shrink-0 px-1 py-0.5 rounded text-[8px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-300">UPD</span>}
                </div>

                {/* Row 2: Details */}
                {hasDetails && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50/30">
                        {checkpoint.requirement != null && checkpoint.requirement !== '' && (
                            <div className="flex items-baseline gap-1.5 min-w-0 max-w-[220px] sm:max-w-none">
                                <span className={metaLabel}>Req</span>
                                <span className={`${metaValue} truncate`} title={checkpoint.requirement}>{checkpoint.requirement}</span>
                            </div>
                        )}
                        {checkpoint.taskType && checkpoint.taskType.length > 0 && (
                            <div className="flex items-center gap-1.5 shrink-0">
                                <span className={metaLabel}>Task</span>
                                {checkpoint.taskType.map((t) => (
                                    <span key={t} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-700 text-[9px] font-semibold">
                                        {taskTypeLabels[t]?.icon}
                                        {taskTypeLabels[t]?.label ?? t}
                                    </span>
                                ))}
                            </div>
                        )}
                        {checkpoint.executionPhase != null && checkpoint.executionPhase !== '' && (
                            <div className="flex items-baseline gap-1 shrink-0">
                                <span className={metaLabel}>Phase</span>
                                <span className={`${metaValue} truncate max-w-[100px]`} title={checkpoint.executionPhase}>{checkpoint.executionPhase}</span>
                            </div>
                        )}
                        {checkpoint.priority != null && checkpoint.priority !== '' && (
                            <div className="flex items-baseline gap-1 shrink-0">
                                <span className={metaLabel}>Priority</span>
                                <span className={`${metaValue} capitalize`}>{checkpoint.priority}</span>
                            </div>
                        )}
                        {checkpoint.executionTime != null && checkpoint.executionTime !== '' && (
                            <div className="flex items-center gap-1 shrink-0">
                                <Timer className="w-3 h-3 text-slate-500 shrink-0" />
                                <span className={metaLabel}>Time</span>
                                <span className={metaValue}>{checkpoint.executionTime}</span>
                            </div>
                        )}
                        {checkpoint.bufferTimeDays != null && (
                            <div className="flex items-baseline gap-1 shrink-0">
                                <span className={metaLabel}>Buffer</span>
                                <span className={metaValue}>{checkpoint.bufferTimeDays}d</span>
                            </div>
                        )}
                        {checkpoint.checkpointCode != null && checkpoint.checkpointCode !== '' && (
                            <div className="flex items-baseline gap-1 shrink-0">
                                <span className={metaLabel}>Code</span>
                                <span className={`${metaValue} font-mono`}>{checkpoint.checkpointCode}</span>
                            </div>
                        )}
                        {checkpoint.isImageRequired === true && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-slate-600 shrink-0"><Image className="w-2.5 h-2.5" /> Image</span>
                        )}
                        {checkpoint.isInputRequired != null && checkpoint.isInputRequired !== 'None' && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-slate-600 shrink-0"><FileText className="w-2.5 h-2.5" /> Input: {checkpoint.isInputRequired}</span>
                        )}
                    </div>
                )}

                {/* Modified from master */}
                {checkpoint.isModifiedFromMaster && checkpoint.originalValues && (checkpoint.modifiedFields?.length ?? 0) > 0 && (
                    <div className="px-3 py-1.5 border-b border-amber-100 bg-amber-50/60 border-l-2 border-amber-500">
                        <span className="text-[9px] font-bold text-amber-800 uppercase tracking-wider mr-2">Modified:</span>
                        <span className="text-[10px] text-slate-700">
                            {(checkpoint.modifiedFields || []).slice(0, 4).map((field) => (
                                <span key={field} className="mr-2">
                                    <span className="text-slate-500 capitalize">{field}</span>
                                    <span className="text-amber-700 mx-0.5">{(checkpoint.originalValues as Record<string, unknown>)[field] as string ?? '—'}</span>
                                    <span className="text-slate-400">→</span>
                                    <span className="text-slate-800 font-medium">
                                        {field === 'name' ? checkpoint.name : field === 'frequency' ? checkpoint.frequency : field === 'requirement' ? checkpoint.requirement : (checkpoint as unknown as Record<string, unknown>)[field] as string ?? '—'}
                                    </span>
                                </span>
                            ))}
                            {(checkpoint.modifiedFields?.length ?? 0) > 4 && <span className="text-slate-500">+{(checkpoint.modifiedFields?.length ?? 0) - 4}</span>}
                        </span>
                    </div>
                )}

                {/* Previously rejected — show change comparison */}
                {wasPreviouslyRejected && (
                    <div className="px-3 py-1.5">
                        <ChangeComparison previousRejectionComment={checkpoint.approverComments || ''} currentValue={`${checkpoint.name} — ${checkpoint.frequency}${checkpoint.requirement ? ` — ${checkpoint.requirement}` : ''}`} type="checkpoint" itemName={checkpoint.name} />
                    </div>
                )}

                {/* Re-approved highlight */}
                {wasReApproved && (
                    <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-200 flex items-center gap-2">
                        <RefreshCw className="w-3 h-3 text-amber-600 shrink-0" />
                        <span className="text-[10px] font-semibold text-amber-800">Re-approved after rejection</span>
                        <span className="text-[9px] text-amber-700 italic truncate" title={checkpoint.approverComments}>Prior: "{checkpoint.approverComments}"</span>
                    </div>
                )}

                {/* Rejection comment (when rejected and not re-approved) */}
                {isRejected && hasApproverComment && !wasPreviouslyRejected && (
                    <div className="px-3 py-1.5 bg-red-50 border-t border-red-200 flex items-start gap-2">
                        <MessageSquare className="w-3 h-3 text-red-500 shrink-0 mt-0.5" />
                        <span className="text-[10px] text-red-800 leading-snug">{checkpoint.approverComments}</span>
                    </div>
                )}

                {/* Decision bar */}
                {isActionable && (
                    <div className="px-3 py-2 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between gap-3" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Decision</span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); if (!isApproved) onAction(checkpoint._id, checkpoint.name, 'approve'); }}
                                disabled={isApproved}
                                className={isApproved ? APPROVE_BTN_ACTIVE : APPROVE_BTN_INACTIVE}
                                title={isApproved ? 'Already approved' : 'Approve this checkpoint'}
                            >
                                <CheckCircle className="w-4 h-4 shrink-0" />
                                <span>{isApproved ? 'Approved ✓' : 'Approve'}</span>
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); if (!isRejected) onAction(checkpoint._id, checkpoint.name, 'reject'); }}
                                disabled={isRejected}
                                className={isRejected ? REJECT_BTN_ACTIVE : REJECT_BTN_INACTIVE}
                                title={isRejected ? 'Already rejected' : 'Reject this checkpoint'}
                            >
                                <XCircle className="w-4 h-4 shrink-0" />
                                <span>{isRejected ? 'Rejected ✗' : 'Reject'}</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="px-3 py-1 h-6 flex items-center flex-wrap gap-x-3 text-[9px] font-medium text-slate-500 bg-slate-100/60 rounded-b-xl">
                    {(checkpoint.lastModifiedByName || checkpoint.lastModifiedAt) && (
                        <span className="truncate">By <span className="text-slate-600 font-semibold">{checkpoint.lastModifiedByName ?? '—'}</span>{checkpoint.lastModifiedAt ? ` · ${formatCheckpointDate(checkpoint.lastModifiedAt)}` : ''}</span>
                    )}
                </div>
            </div>
        );
    };

export const ActionModal: React.FC<{
    open: boolean;
    type: 'checkpoint' | 'equipment' | 'resource' | 'asset' | 'master-checkpoints';
    action: 'approve' | 'reject';
    itemName: string;
    comments: string;
    setComments: (value: string) => void;
    onConfirm: () => void;
    onCancel: () => void;
    loading: boolean;
}> = ({ open, type, action, itemName, comments, setComments, onConfirm, onCancel, loading }) => {
    if (!open) return null;

    const typeLabel = type === 'checkpoint' ? 'Checkpoint' :
        type === 'equipment' ? 'Equipment' :
            type === 'resource' ? 'Resource Assignment' :
                'Master Checkpoints';

    const isApprove = action === 'approve';
    const isReject = action === 'reject';

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]" onClick={onCancel} />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                    <div className={`px-6 py-4 border-b border-gray-200 ${isApprove ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex items-center space-x-2">
                            {isApprove ? (
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            ) : (
                                <XCircle className="w-6 h-6 text-red-600" />
                            )}
                            <h3 className="text-lg font-semibold text-gray-900">
                                {isApprove ? 'Approve' : 'Reject'} {typeLabel}
                            </h3>
                        </div>
                    </div>
                    <div className="px-6 py-4">
                        <p className="text-gray-700 mb-4">
                            {isApprove ? (
                                <>
                                    You are about to approve <strong className="text-green-600">{itemName}</strong>.
                                    {type === 'resource' && ' You can optionally add comments.'}
                                </>
                            ) : (
                                <>
                                    You are about to reject <strong className="text-red-600">{itemName}</strong>.
                                    Please provide a detailed reason for rejection.
                                </>
                            )}
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {isApprove ? 'Comments (Optional)' : 'Rejection Reason'}
                                {isReject && <span className="text-red-500">*</span>}
                            </label>
                            <textarea
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                rows={4}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ${isApprove ? 'focus:ring-green-500' : 'focus:ring-red-500'
                                    }`}
                                placeholder={isApprove
                                    ? "Add any comments about this approval (optional)..."
                                    : "Please explain why this item is being rejected..."
                                }
                                required={isReject}
                                autoFocus
                            />
                            {isReject && !comments.trim() && (
                                <p className="text-xs text-gray-500 mt-1">Rejection reason is required</p>
                            )}
                        </div>
                    </div>
                    <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50">
                        <button
                            onClick={onCancel}
                            disabled={loading}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading || (isReject && !comments.trim())}
                            className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium ${isApprove
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-red-600 hover:bg-red-700'
                                }`}
                        >
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {isApprove ? (
                                <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Approve Item
                                </>
                            ) : (
                                <>
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reject Item
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export const OverviewTab: React.FC<{
    overview: ProjectApprovalDetails['overview'];
    assets?: AssetApprovalDetail[];
    resources?: ProjectApprovalDetails['resources'];
    isAdmin?: boolean;
    onAdminApproveAndGenerate?: () => Promise<void>;
    bulkLoading?: boolean;
    /** Show project-level Approve/Reject buttons (for approvers) */
    isHead?: boolean;
    isReviewSubmitted?: boolean;
    /** Whether all checkpoints/resources have been reviewed (100% progress) */
    isReviewComplete?: boolean;
    onApproveProject?: () => void;
    onRejectProject?: () => void;
    approvingProject?: boolean;
}> = ({
    overview,
    assets = [],
    resources,
    isAdmin = false,
    onAdminApproveAndGenerate,
    bulkLoading = false,
    isHead = false,
    isReviewSubmitted = false,
    isReviewComplete = false,
    onApproveProject,
    onRejectProject,
    approvingProject = false,
}) => {
        const totalTeam =
            (overview.assignedResources.siteSupervisors?.length || 0) +
            (overview.assignedResources.siteEngineers?.length || 0);
        const supervisorCount = overview.assignedResources.siteSupervisors?.length || 0;
        const engineerCount = overview.assignedResources.siteEngineers?.length || 0;

        const head = overview.assignedResources.projectHead || null;
        const manager = overview.assignedResources.projectManager || null;
        const supervisors = resources?.hierarchy || [];
        const managedEngineers = supervisors.flatMap((sup) => sup.subordinates || []);
        const independentEngineers = resources?.independentEngineers || [];
        const allEngineers = [...managedEngineers, ...independentEngineers];

        const allCheckpoints = assets.flatMap((asset) => [
            ...asset.checkpoints.masterCheckpoints,
            ...asset.checkpoints.projectLevelCheckpoints,
        ]);
        const checkpointsApproved = allCheckpoints.filter(
            (cp) => cp.approvalStatus === 'approved',
        ).length;

        const allResourceEntries: ResourceApprovalDetail[] = [];
        if (resources) {
            (resources.hierarchy || []).forEach((sup) => {
                allResourceEntries.push(sup);
                (sup.subordinates || []).forEach((eng) => allResourceEntries.push(eng));
            });
            (resources.independentEngineers || []).forEach((eng) =>
                allResourceEntries.push(eng),
            );
        }

        const isResubmitted =
            overview.status === 'pending_approval' &&
            (overview.projectLevelApproverComments || overview.projectLevelRejectedAt);

        const journey: JourneyStepConfig[] = (() => {
            const steps: JourneyStepConfig[] = [
                {
                    key: 'submitted',
                    title: 'Submitted',
                    description: overview.requester
                        ? `By ${overview.requester.name}`
                        : 'Project submitted for approval',
                    time: overview.createdAt
                        ? new Date(overview.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                        })
                        : undefined,
                    tone: 'completed',
                },
            ];

            if (overview.status === 'approved') {
                steps.push({
                    key: 'approved',
                    title: 'Approved',
                    description:
                        overview.projectLevelApproverComments || 'Project approved successfully',
                    time: overview.projectLevelApprovedAt
                        ? new Date(overview.projectLevelApprovedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })
                        : undefined,
                    tone: 'completed',
                });
            } else if (overview.status === 'rejected') {
                steps.push({
                    key: 'rejected',
                    title: 'Rejected',
                    description:
                        overview.projectLevelApproverComments || 'Project rejected by approver',
                    time: overview.projectLevelRejectedAt
                        ? new Date(overview.projectLevelRejectedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })
                        : undefined,
                    tone: 'rejected',
                });
            } else {
                steps.push({
                    key: 'under-review',
                    title: isResubmitted ? 'Resubmitted' : 'Under Review',
                    description: isResubmitted
                        ? 'Updated submission awaiting approval'
                        : 'Pending approver decision',
                    tone: 'active',
                });
            }

            return steps;
        })();

        const toHierarchyPerson = (entity?: {
            _id?: string;
            id?: string;
            name: string;
            email?: string;
            role?: { name?: string };
        }): HierarchyPerson | null => {
            if (!entity?.name) return null;
            return {
                id: entity._id || entity.id || entity.name,
                name: entity.name,
                email: entity.email,
                subtitle: entity.role?.name,
            };
        };

        const hierarchyLevels: HierarchyLevel[] = [
            {
                key: 'head',
                title: 'Project Head',
                accent: 'from-blue-500 to-blue-600',
                people: head ? [toHierarchyPerson(head)!] : [],
            },
            {
                key: 'manager',
                title: 'Project Manager',
                accent: 'from-indigo-500 to-indigo-600',
                people: manager ? [toHierarchyPerson(manager)!] : [],
            },
            {
                key: 'supervisors',
                title: 'Site Supervisors',
                accent: 'from-purple-500 to-purple-600',
                people: supervisors
                    .map((sup) => toHierarchyPerson(sup))
                    .filter(Boolean) as HierarchyPerson[],
            },
            {
                key: 'engineers',
                title: 'Site Engineers',
                accent: 'from-cyan-500 to-cyan-600',
                people: allEngineers
                    .map((eng) => toHierarchyPerson(eng))
                    .filter(Boolean) as HierarchyPerson[],
            },
        ];

        const hasDocuments = !!(overview.contractDocument || (overview.attachedDocuments && overview.attachedDocuments.length > 0));
        // Show Overview approve/reject only when head, review is 100% complete, not yet submitted, and project not already approved/rejected at project level
        const projectLevelDecided = overview.status === 'approved' || overview.status === 'rejected';
        const showOverviewActions = isHead && isReviewComplete && !isReviewSubmitted && !projectLevelDecided && (onApproveProject || onRejectProject);

        return (
            <div className="space-y-4 pb-24">
                {/* 1. Hero — modern: soft gradient, ring, rounded-3xl, refined KPIs */}
                <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-white via-white to-slate-50/60 border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)] ring-1 ring-slate-900/[0.02]">
                    <div className="p-6 sm:p-8 flex flex-col lg:flex-row lg:items-stretch gap-6 lg:gap-8">
                        {/* Left: project name, timeline, requester, resubmission */}
                        <div className="min-w-0 flex-1 lg:w-1/2 lg:flex-none">
                            <div className="border-l-2 border-slate-200 pl-4">
                                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                                    {overview.projectName || 'Project'}
                                </h1>
                                <p className="mt-2 text-sm text-slate-500 font-medium">
                                    {[overview.projectCode, overview.projectId, overview.category].filter(Boolean).join(' · ') || '—'}
                                </p>
                            </div>
                            {(overview.timeline?.startDate || overview.timeline?.endDate) && (
                                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-100/80 px-4 py-2 text-sm text-slate-700 font-medium">
                                    <span>
                                        {overview.timeline.startDate && new Date(overview.timeline.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                    <span className="text-slate-400">→</span>
                                    <span>
                                        {overview.timeline.endDate && new Date(overview.timeline.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                    {overview.timeline.duration != null && (
                                        <span className="text-slate-400">({overview.timeline.duration} mo)</span>
                                    )}
                                </div>
                            )}
                            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-slate-500">
                                {overview.requester && (
                                    <span className="inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-1.5">
                                        <User className="h-4 w-4 text-slate-400" />
                                        <span>Requested by <span className="font-medium text-slate-700">{overview.requester.name}</span></span>
                                        {overview.requester.email && <span className="text-slate-400 hidden sm:inline">· {overview.requester.email}</span>}
                                    </span>
                                )}
                                {overview.createdAt && (
                                    <span className="rounded-lg bg-slate-50 px-3 py-1.5 font-medium text-slate-600">
                                        {new Date(overview.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                )}
                            </div>
                            {overview.resubmissionComments && (
                                <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 to-amber-50/50 p-4 shadow-sm">
                                    <AlertCircle className="h-5 w-5 shrink-0 text-amber-500 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">Resubmission note</p>
                                        <p className="mt-1.5 text-sm text-amber-900/90 leading-relaxed">{overview.resubmissionComments}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Right: KPIs — horizontal, modern cards with accent */}
                        <div className="flex flex-row flex-wrap items-stretch gap-3 lg:w-1/2 lg:flex-none lg:min-w-0">
                            <div className="flex flex-1 min-w-0 items-center gap-3 rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50/80 to-white py-3.5 px-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-500 text-white shadow-lg shadow-sky-500/25">
                                    <Package className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-sky-600/90">Assets</p>
                                    <p className="text-xl font-bold tabular-nums text-slate-900">{overview.totalAssets}</p>
                                </div>
                            </div>
                            <div className="flex flex-1 min-w-0 items-center gap-3 rounded-2xl border border-violet-100 bg-gradient-to-br from-violet-50/80 to-white py-3.5 px-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-500 text-white shadow-lg shadow-violet-500/25">
                                    <FileCheck className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-violet-600/90">Checkpoints</p>
                                    <p className="text-xl font-bold tabular-nums text-slate-900">{overview.totalCheckpoints}</p>
                                    {allCheckpoints.length > 0 && (
                                        <p className="text-xs text-slate-500 font-medium">{checkpointsApproved}/{allCheckpoints.length}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-1 min-w-0 items-center gap-3 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white py-3.5 px-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/25">
                                    <HardDrive className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600/90">Equipment</p>
                                    <p className="text-xl font-bold tabular-nums text-slate-900">{overview.totalEquipment}</p>
                                </div>
                            </div>
                            <div className="flex flex-1 min-w-0 items-center gap-3 rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50/80 to-white py-3.5 px-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-500 text-white shadow-lg shadow-amber-500/25">
                                    <Users className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600/90">Resources</p>
                                    <p className="text-xl font-bold tabular-nums text-slate-900">{totalTeam}</p>
                                    <p className="text-xs text-slate-500 font-medium">{supervisorCount} sup · {engineerCount} eng</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Two-column: Left (Location, Team, Documents) | Right (Journey, Approvers) */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <div className="space-y-4 lg:col-span-2">
                        {/* Location card */}
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                                    <MapPin className="h-4 w-4" />
                                </div>
                                <h3 className="text-sm font-semibold text-slate-900">Location</h3>
                            </div>
                            {overview.location?.name || overview.location?.address ? (
                                <div className="space-y-1">
                                    {overview.location.name && <p className="text-base font-semibold text-slate-900">{overview.location.name}</p>}
                                    {overview.location.address && <p className="text-sm text-slate-600">{overview.location.address}</p>}
                                    {((overview.location as { latitude?: string; longitude?: string }).latitude || (overview.location as { latitude?: string; longitude?: string }).longitude) && (
                                        <p className="text-xs font-mono text-slate-500">
                                            {(overview.location as { latitude?: string }).latitude} {(overview.location as { longitude?: string }).longitude}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 px-3 py-3">
                                    <MapPin className="h-4 w-4 text-slate-300 shrink-0" />
                                    <p className="text-sm text-slate-500">Not specified</p>
                                </div>
                            )}
                        </div>

                        {/* Team — graphical tree: pictorial icons, SVG connectors, animations */}
                        <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/30 p-5 shadow-sm ring-1 ring-slate-900/[0.02] overflow-hidden">
                            <style>{`
                                @keyframes teamNodeEnter { from { opacity: 0; transform: translateY(-8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
                                @keyframes connectorFade { from { opacity: 0; transform: scaleY(0.3); } to { opacity: 1; transform: scaleY(1); } }
                                .team-node { animation: teamNodeEnter 0.4s ease-out both; }
                                .team-node:hover { transform: translateY(0) scale(1.02); box-shadow: 0 10px 25px -5px rgb(0 0 0 / 0.08); }
                                .team-connector { transform-origin: top; animation: connectorFade 0.35s ease-out both; }
                            `}</style>
                            <div className="flex items-center gap-2 mb-4">
                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/20">
                                    <Users className="h-4 w-4" />
                                </div>
                                <h3 className="text-base font-bold text-slate-900 tracking-tight">Team</h3>
                            </div>
                            <div className="relative flex flex-col items-center">
                                {/* Level 1: Project Head */}
                                {head && (
                                    <div className="flex flex-col items-center w-full">
                                        <div className="team-node flex items-center gap-3 rounded-2xl border-2 border-blue-200/70 bg-gradient-to-br from-blue-50 to-white py-3.5 px-4 shadow-md hover:shadow-lg transition-all duration-300 w-full max-w-sm" style={{ animationDelay: '0ms' }}>
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30">
                                                <Crown className="h-6 w-6" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-bold uppercase tracking-wider text-blue-600/90">Project Head</p>
                                                <p className="text-sm font-bold text-slate-900 truncate">{head.name}</p>
                                                {head.email && <p className="text-xs text-slate-600 truncate">{head.email}</p>}
                                            </div>
                                        </div>
                                        <div className="team-connector w-0.5 h-6 bg-gradient-to-b from-slate-300 to-slate-200 rounded-full shrink-0" style={{ animationDelay: '0.25s' }} aria-hidden />
                                    </div>
                                )}
                                {/* Level 2: Project Manager */}
                                {manager && (
                                    <div className="flex flex-col items-center w-full">
                                        <div className="team-node flex items-center gap-3 rounded-2xl border-2 border-indigo-200/70 bg-gradient-to-br from-indigo-50 to-white py-3.5 px-4 shadow-md hover:shadow-lg transition-all duration-300 w-full max-w-sm" style={{ animationDelay: '80ms' }}>
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/30">
                                                <Briefcase className="h-6 w-6" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-bold uppercase tracking-wider text-indigo-600/90">Project Manager</p>
                                                <p className="text-sm font-bold text-slate-900 truncate">{manager.name}</p>
                                                {manager.email && <p className="text-xs text-slate-600 truncate">{manager.email}</p>}
                                            </div>
                                        </div>
                                        <div className="team-connector w-0.5 h-5 bg-gradient-to-b from-slate-300 to-slate-200 rounded-full shrink-0" style={{ animationDelay: '0.35s' }} aria-hidden />
                                    </div>
                                )}
                                {/* Level 3 & 4: Supervisors → Engineers (tree branches) */}
                                {supervisors.length > 0 && (
                                    <div className="w-full flex flex-col items-center">
                                        {/* Tree connector: vertical line down from manager, then horizontal branch */}
                                        <div className="team-connector flex justify-center w-full mb-1" style={{ animationDelay: '0.45s' }}>
                                            <svg className="h-5 w-8 shrink-0 text-slate-300" viewBox="0 0 32 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
                                                <path d="M16 0 v 10 M16 10 h -16 M16 10 h 16 M16 10 v 10" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {(supervisors as Array<{ name: string; email?: string; _id?: string; subordinates?: Array<{ name: string; email?: string; _id?: string }> }>).map((sup, idx) => (
                                                <div key={sup._id || sup.name || idx} className="flex flex-col items-center">
                                                    <div className="team-connector w-0.5 h-3 bg-gradient-to-b from-slate-300 to-transparent rounded-full mb-0.5 shrink-0" style={{ animationDelay: `${0.5 + idx * 0.06}s` }} aria-hidden />
                                                    <div className="team-node w-full flex items-center gap-2.5 rounded-xl border-2 border-purple-200/70 bg-gradient-to-br from-purple-50 to-white py-2.5 px-3 shadow-sm hover:shadow-md hover:border-purple-300/80 transition-all duration-300" style={{ animationDelay: `${120 + idx * 60}ms` }}>
                                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md">
                                                            <Users2 className="h-5 w-5" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-[10px] font-bold uppercase tracking-wider text-purple-600/90">Supervisor</p>
                                                            <p className="text-sm font-semibold text-slate-900 truncate">{sup.name}</p>
                                                            {sup.email && <p className="text-[10px] text-slate-500 truncate">{sup.email}</p>}
                                                        </div>
                                                    </div>
                                                    {(sup.subordinates && sup.subordinates.length > 0) && (
                                                        <>
                                                            <div className="team-connector w-0.5 h-3 my-1 bg-gradient-to-b from-slate-200 to-transparent rounded-full shrink-0 self-center" aria-hidden />
                                                            <div className="w-full space-y-1.5 pl-2 border-l-2 border-cyan-200/80">
                                                                {sup.subordinates.map((eng, eIdx) => (
                                                                    <div key={eng._id || eng.name || eIdx} className="team-node flex items-center gap-2 rounded-lg border border-cyan-100 bg-gradient-to-r from-cyan-50/80 to-white py-2 px-2.5 shadow-sm hover:shadow hover:border-cyan-200 transition-all duration-200" style={{ animationDelay: `${180 + idx * 60 + eIdx * 40}ms` }}>
                                                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 text-white"><Wrench className="h-4 w-4" /></div>
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="text-[10px] font-semibold text-cyan-700/90">Engineer</p>
                                                                            <p className="text-xs font-medium text-slate-800 truncate">{eng.name}</p>
                                                                            {eng.email && <p className="text-[9px] text-slate-500 truncate">{eng.email}</p>}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        {independentEngineers.length > 0 && (
                                            <div className="w-full mt-4 pt-4 border-t border-slate-200">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 text-center">Independent Engineers</p>
                                                <div className="flex flex-wrap justify-center gap-2">
                                                    {independentEngineers.slice(0, 6).map((eng, eIdx) => (
                                                        <div key={(eng as { _id?: string })._id || eng.name || eIdx} className="team-node flex items-center gap-2 rounded-lg border border-cyan-100 bg-cyan-50/60 py-2 px-2.5 shadow-sm hover:shadow transition-all duration-200" style={{ animationDelay: `${300 + eIdx * 40}ms` }}>
                                                            <Wrench className="h-4 w-4 text-cyan-600 shrink-0" />
                                                            <div className="min-w-0"><p className="text-xs font-medium text-slate-800 truncate max-w-[100px]">{eng.name}</p></div>
                                                        </div>
                                                    ))}
                                                    {independentEngineers.length > 6 && <span className="text-xs text-slate-500 self-center">+{independentEngineers.length - 6}</span>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {supervisors.length === 0 && independentEngineers.length > 0 && (
                                    <div className="w-full mt-2 flex flex-col items-center">
                                        <div className="team-connector w-0.5 h-4 bg-gradient-to-b from-slate-300 to-slate-200 rounded-full mb-2 shrink-0" style={{ animationDelay: '0.4s' }} aria-hidden />
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 text-center">Engineers</p>
                                        <div className="flex flex-wrap justify-center gap-2">
                                            {independentEngineers.slice(0, 6).map((eng, eIdx) => (
                                                <div key={(eng as { _id?: string })._id || eng.name || eIdx} className="team-node flex items-center gap-2 rounded-lg border border-cyan-100 bg-cyan-50/60 py-2 px-2.5 shadow-sm hover:shadow transition-all duration-200" style={{ animationDelay: `${eIdx * 50}ms` }}>
                                                    <Wrench className="h-4 w-4 text-cyan-600 shrink-0" />
                                                    <div className="min-w-0"><p className="text-xs font-medium text-slate-800 truncate max-w-[100px]">{eng.name}</p></div>
                                                </div>
                                            ))}
                                            {independentEngineers.length > 6 && <span className="text-xs text-slate-500 self-center">+{independentEngineers.length - 6}</span>}
                                        </div>
                                    </div>
                                )}
                                {!head && !manager && supervisors.length === 0 && allEngineers.length === 0 && (
                                    <p className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 py-8 text-center text-sm text-slate-500 font-medium">No team assigned</p>
                                )}
                            </div>
                        </div>

                        {/* Documents card */}
                        {hasDocuments && (
                            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                                        <FileText className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-900">Documents</h3>
                                        <p className="text-xs text-slate-500">Uploaded at project creation</p>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {overview.contractDocument && (
                                        <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                                                <span className="text-sm font-medium text-slate-800">Contract / primary document</span>
                                            </div>
                                            <span className="shrink-0 rounded bg-blue-200/80 px-2 py-0.5 text-[10px] font-semibold text-blue-800">Contract</span>
                                            <button type="button" onClick={() => void fileService.openMediaInNewTab(overview.contractDocument)} className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">
                                                <Download className="h-3.5 w-3.5" /> Download
                                            </button>
                                        </div>
                                    )}
                                    {(overview.attachedDocuments && overview.attachedDocuments.length > 0) && overview.attachedDocuments.map((doc, idx) => (
                                        <div key={idx} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                                                <div>
                                                    <p className="text-sm font-medium text-slate-800 truncate">{doc.documentName}</p>
                                                    {doc.documentType && doc.documentType !== 'Other' && <p className="text-xs text-slate-500">{doc.documentType}</p>}
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => void fileService.openMediaInNewTab(doc.documentFile)} className="shrink-0 inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
                                                <Download className="h-3 w-3" /> Download
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        {/* Approval journey — horizontal */}
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                                    <Clock className="h-4 w-4" />
                                </div>
                                <h3 className="text-sm font-semibold text-slate-900">Approval journey</h3>
                            </div>
                            <ApprovalTimelineHorizontal steps={journey} />
                        </div>

                        {/* Key approvers — with avatar */}
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                                    <Shield className="h-4 w-4" />
                                </div>
                                <h3 className="text-sm font-semibold text-slate-900">Key approvers</h3>
                            </div>
                            <div className="space-y-2">
                                {head ? <ApproverTile role="Project Head" person={{ name: head.name, email: head.email }} /> : <EmptyApprover role="Project Head" />}
                                {manager ? <ApproverTile role="Project Manager" person={{ name: manager.name, email: manager.email }} /> : <EmptyApprover role="Project Manager" />}
                            </div>
                        </div>

                        {overview.customer && (
                            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                <InfoTile label="Customer" value={overview.customer.name} />
                            </div>
                        )}
                    </div>
                </div>

                {/* Project-level approved banner — shown after Overview is approved, Assets/Resources still actionable */}
                {isHead && overview.status === 'approved' && (
                    <div className="sticky bottom-0 z-10 -mx-5 -mb-4 mt-4 border-t border-emerald-200 bg-emerald-50 px-5 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.06)]">
                        <div className="flex flex-wrap items-center justify-between gap-3 max-w-6xl mx-auto">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold text-emerald-800">Project overview approved</p>
                                    <p className="text-xs text-emerald-700">Switch to <strong>Assets</strong> and <strong>Assignments</strong> tabs to approve/reject individual items.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sticky action bar — inside scroll container */}
                {showOverviewActions && (
                    <div className="sticky bottom-0 z-10 -mx-5 -mb-4 mt-4 border-t border-slate-200 bg-white px-5 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                        <div className="flex flex-wrap items-center justify-between gap-3 max-w-6xl mx-auto">
                            <p className="text-sm text-slate-600">Approve the project overview. Assets &amp; assignments are approved separately in their tabs.</p>
                            <div className="flex items-center gap-3">
                                {onRejectProject && (
                                    <button type="button" onClick={onRejectProject} className="inline-flex items-center gap-2 rounded-lg border-2 border-red-200 bg-white px-5 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50">
                                        <XCircle className="h-4 w-4" /> Reject project
                                    </button>
                                )}
                                {onApproveProject && (
                                    <button type="button" onClick={onApproveProject} disabled={approvingProject} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                                        {approvingProject ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                                        {approvingProject ? 'Approving...' : 'Approve project overview'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

export const AssetsTab: React.FC<{
    assets: AssetApprovalDetail[];
    expandedAssets: Set<string>;
    expandedEquipment: Set<string>;
    toggleAssetExpand: (id: string) => void;
    toggleEquipmentExpand: (id: string) => void;
    onCheckpointAction: (id: string, name: string, action: 'approve' | 'reject') => Promise<void>;
    onEquipmentAction: (id: string, name: string, action: 'approve' | 'reject') => void;
    onAssetAction: (assetId: string, assetName: string, action: 'approve' | 'reject') => void;
    getAssetRejectedCheckpointCount: (asset: AssetApprovalDetail) => number;
    isReviewSubmitted?: boolean;
    isResubmitted?: boolean;
    isHead?: boolean;
    selectedCheckpoints: Set<string>;
    toggleCheckpointSelection: (id: string) => void;
    selectAllCheckpointsInAsset: (asset: AssetApprovalDetail) => void;
    selectAllCheckpointsInFrequency?: (asset: AssetApprovalDetail, frequency: string) => void;
    selectAllCheckpointsInProjectLevelNew?: (asset: AssetApprovalDetail) => void;
    selectAllPendingCheckpoints?: () => void;
    clearCheckpointSelections: () => void;
    onBulkCheckpointAction: (action: 'approve' | 'reject') => Promise<void>;
}> = ({
    assets,
    expandedAssets,
    expandedEquipment,
    toggleAssetExpand,
    toggleEquipmentExpand,
    onCheckpointAction,
    onEquipmentAction,
    onAssetAction,
    getAssetRejectedCheckpointCount,
    isReviewSubmitted = false,
    isResubmitted = false,
    isHead = false,
    selectedCheckpoints,
    toggleCheckpointSelection,
    selectAllCheckpointsInAsset,
    selectAllCheckpointsInFrequency,
    selectAllCheckpointsInProjectLevelNew,
    selectAllPendingCheckpoints,
    clearCheckpointSelections,
    onBulkCheckpointAction,
}) => {
        if (assets.length === 0) {
            return (
                <div className="text-center py-16">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No assets found for this project</p>
                </div>
            );
        }

        const pendingCount = useMemo(() => {
            const projectIsResubmitted = isResubmitted;
            let count = 0;
            assets.forEach(asset => {
                asset.checkpoints.masterCheckpoints.forEach(cp => {
                    if (cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (projectIsResubmitted && cp.approvalStatus === 'rejected')) count++;
                });
                asset.checkpoints.projectLevelCheckpoints.forEach(cp => {
                    if (cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (projectIsResubmitted && cp.approvalStatus === 'rejected')) count++;
                });
            });
            return count;
        }, [assets, isResubmitted]);

        const PROJECT_LEVEL_NEW_TAB = 'ProjectLevelNew' as const;
        const [selectedFrequencyByAsset, setSelectedFrequencyByAsset] = useState<Record<string, string | 'All' | typeof PROJECT_LEVEL_NEW_TAB>>({});

        const setAssetFrequency = (assetId: string, freq: string | 'All' | typeof PROJECT_LEVEL_NEW_TAB) => {
            setSelectedFrequencyByAsset(prev => ({ ...prev, [assetId]: freq }));
        };

        return (
            <div className="space-y-3">
                {/* Selection & bulk actions bar — modern header with animated buttons */}
                {isHead && !isReviewSubmitted && (pendingCount > 0 || selectedCheckpoints.size > 0) && (
                    <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border border-slate-200 rounded-xl shadow-md px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            {selectedCheckpoints.size > 0 ? (
                                <span className="text-sm font-semibold text-slate-800 tabular-nums flex items-center gap-1.5">
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold">{selectedCheckpoints.size}</span>
                                    selected
                                </span>
                            ) : (
                                <span className="text-sm text-slate-600">
                                    {pendingCount} pending · Select cards or use <strong>Select all pending</strong>
                                </span>
                            )}
                            {selectAllPendingCheckpoints && pendingCount > 0 && (
                                <button
                                    type="button"
                                    onClick={selectAllPendingCheckpoints}
                                    className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-800 bg-slate-100 border border-slate-300 rounded-xl hover:bg-slate-200 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-sm"
                                    title="Select all pending checkpoints"
                                >
                                    <Square className="w-4 h-4 text-slate-600" />
                                    Select all pending
                                </button>
                            )}
                            {selectedCheckpoints.size > 0 && (
                                <button
                                    type="button"
                                    onClick={clearCheckpointSelections}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200"
                                >
                                    Clear selection
                                </button>
                            )}
                        </div>
                        {selectedCheckpoints.size > 0 && (
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => onBulkCheckpointAction('approve')}
                                    disabled={isReviewSubmitted}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 hover:scale-[1.03] active:scale-[0.98] rounded-xl border border-emerald-700/50 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-200"
                                >
                                    <CheckCircle className="w-5 h-5" />
                                    Approve selected
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onBulkCheckpointAction('reject')}
                                    disabled={isReviewSubmitted}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 hover:scale-[1.03] active:scale-[0.98] rounded-xl border border-red-700/50 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-200"
                                >
                                    <XCircle className="w-5 h-5" />
                                    Reject selected
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Assets — only show assets that have checkpoints; each has its own frequency tabs in header */}
                <div className="space-y-4">
                    {assets.filter((asset) => {
                        const total = asset.checkpoints.masterCheckpoints.length + asset.checkpoints.projectLevelCheckpoints.length;
                        return total > 0;
                    }).map((asset) => {
                        const rejectedCount = getAssetRejectedCheckpointCount(asset);
                        const hasRejections = rejectedCount >= 2;
                        const allCheckpoints = [
                            ...asset.checkpoints.masterCheckpoints,
                            ...asset.checkpoints.projectLevelCheckpoints
                        ];
                        const approvedCheckpoints = allCheckpoints.filter(cp => cp.approvalStatus === 'approved').length;
                        const pendingCheckpoints = allCheckpoints.filter(cp => cp.approvalStatus === 'pending').length;
                        const isEffectivelyApproved = allCheckpoints.length > 0 &&
                            approvedCheckpoints === allCheckpoints.length &&
                            pendingCheckpoints === 0 &&
                            rejectedCount === 0;
                        const effectiveAssetStatus = isEffectivelyApproved ? 'approved' : asset.approvalStatus;
                        const pct = asset.totalCheckpoints ? Math.round((approvedCheckpoints / asset.totalCheckpoints) * 100) : 0;

                        return (
                            <div
                                key={asset._id}
                                className={`rounded-lg border bg-white transition-shadow ${hasRejections ? 'border-amber-300 ring-1 ring-amber-200/50' : 'border-slate-200'}`}
                            >
                                {/* Asset header box — name + stats, then frequency tabs for this asset */}
                                <div className="bg-slate-50/80 border-b border-slate-100 rounded-t-lg overflow-hidden">
                                    <div className="flex items-center gap-2 px-3 py-2 min-h-[36px]">
                                        <Package className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                        <span className="text-[11px] font-semibold text-slate-800 truncate flex-1 min-w-0" title={asset.assetName}>
                                            {asset.assetName}
                                        </span>
                                        {effectiveAssetStatus === 'approved' && (
                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-semibold rounded flex-shrink-0">
                                                <span className="w-1 h-1 rounded-full bg-emerald-500" /> OK
                                            </span>
                                        )}
                                        {effectiveAssetStatus === 'rejected' && (
                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-100 text-red-700 text-[9px] font-semibold rounded flex-shrink-0">
                                                <span className="w-1 h-1 rounded-full bg-red-500" /> Rej
                                            </span>
                                        )}
                                        {hasRejections && (
                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-semibold rounded flex-shrink-0">
                                                {rejectedCount} Rej
                                            </span>
                                        )}
                                        <span className="text-[9px] text-slate-500 tabular-nums flex-shrink-0">
                                            <span className="text-emerald-600 font-medium">{approvedCheckpoints}</span>/<span>{asset.totalCheckpoints}</span> CP
                                        </span>
                                        <div className="w-14 h-1 bg-slate-200 rounded-full overflow-hidden flex-shrink-0">
                                            <div
                                                className="h-full bg-emerald-500 rounded-full transition-all"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                    {/* Frequency tabs — per asset, pill-style tab bar (matches drawer Overview/Assets/Assignments) */}
                                    {(() => {
                                        const assetFreqSet = new Set(allCheckpoints.map(cp => cp.frequency));
                                        const assetFreqs = [...FREQUENCY_ORDER.filter(f => assetFreqSet.has(f)), ...[...assetFreqSet].filter((f: string) => !FREQUENCY_ORDER.includes(f as (typeof FREQUENCY_ORDER)[number]))];
                                        const assetFreqCounts: Record<string, number> = { All: allCheckpoints.length };
                                        allCheckpoints.forEach(cp => { assetFreqCounts[cp.frequency] = (assetFreqCounts[cp.frequency] || 0) + 1; });
                                        const projectLevelNewCount = allCheckpoints.filter(cp => getCheckpointDisplayGroup(cp) === 'newAtProject').length;
                                        const selFreq = selectedFrequencyByAsset[asset._id] ?? 'All';
                                        return (
                                            <div className="px-3 pb-2 pt-0">
                                                <div className="inline-flex flex-wrap gap-0 p-1 rounded-lg bg-slate-200/60 border border-slate-200">
                                                    <button
                                                        type="button"
                                                        onClick={() => setAssetFrequency(asset._id, 'All')}
                                                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-medium transition-all duration-200 ${
                                                            selFreq === 'All'
                                                                ? 'bg-white text-slate-800 shadow-sm border border-slate-200'
                                                                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                                                        }`}
                                                    >
                                                        All {assetFreqCounts.All ? `(${assetFreqCounts.All})` : ''}
                                                    </button>
                                                    {projectLevelNewCount > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setAssetFrequency(asset._id, PROJECT_LEVEL_NEW_TAB)}
                                                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-medium transition-all duration-200 ${
                                                                selFreq === PROJECT_LEVEL_NEW_TAB
                                                                    ? 'bg-white text-emerald-700 shadow-sm border border-emerald-200'
                                                                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                                                            }`}
                                                        >
                                                            Added project level new ({projectLevelNewCount})
                                                        </button>
                                                    )}
                                                    {assetFreqs.map((freq) => {
                                                        const count = assetFreqCounts[freq] ?? 0;
                                                        const freqStyle = FREQUENCY_COLORS[freq] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
                                                        const isActive = selFreq === freq;
                                                        return (
                                                            <button
                                                                key={freq}
                                                                type="button"
                                                                onClick={() => setAssetFrequency(asset._id, freq)}
                                                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[10px] font-medium transition-all duration-200 ${
                                                                    isActive
                                                                        ? `bg-white shadow-sm border ${freqStyle.text} ${freqStyle.border}`
                                                                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50 border border-transparent'
                                                                }`}
                                                            >
                                                                {freq} {count ? `(${count})` : ''}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Checkpoints — frequency tab filter: show All grouped by freq, or single frequency */}
                                <div className="px-3 pb-2 pt-1.5 space-y-2">
                                    {(() => {
                                        const allCheckpoints = [
                                            ...asset.checkpoints.masterCheckpoints,
                                            ...asset.checkpoints.projectLevelCheckpoints
                                        ];
                                        const selFreqForAsset = selectedFrequencyByAsset[asset._id] ?? 'All';
                                        const filteredByTab = selFreqForAsset === PROJECT_LEVEL_NEW_TAB
                                            ? allCheckpoints.filter(cp => getCheckpointDisplayGroup(cp) === 'newAtProject')
                                            : selFreqForAsset === 'All'
                                                ? allCheckpoints
                                                : allCheckpoints.filter(cp => cp.frequency === selFreqForAsset);
                                        if (filteredByTab.length === 0) return null;

                                        if (selFreqForAsset === PROJECT_LEVEL_NEW_TAB) {
                                            const style = CHECKPOINT_GROUP_STYLES.newAtProject;
                                            const projectLevelNewActionableCount = filteredByTab.filter(cp => cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (isResubmitted && cp.approvalStatus === 'rejected')).length;
                                            return (
                                                <div className={`rounded-lg border p-2 ${style.sectionBorder} ${style.sectionBg}`}>
                                                    <div className="flex items-center justify-between gap-2 mb-1.5 px-0.5">
                                                        <div className="flex items-center gap-1.5">
                                                            <PlusCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                                            <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-800">Added project level new</span>
                                                            <span className="text-[9px] text-slate-500 tabular-nums">({filteredByTab.length})</span>
                                                        </div>
                                                        {isHead && !isReviewSubmitted && selectAllCheckpointsInProjectLevelNew && projectLevelNewActionableCount > 0 && (
                                                            <button type="button" onClick={() => selectAllCheckpointsInProjectLevelNew(asset)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-semibold rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200">Select all</button>
                                                        )}
                                                    </div>
                                                    <CheckpointTimeline
                                                        checkpoints={filteredByTab}
                                                        onAction={onCheckpointAction}
                                                        isSelected={(id) => selectedCheckpoints.has(id)}
                                                        onToggleSelection={isHead ? toggleCheckpointSelection : undefined}
                                                        isReviewSubmitted={isReviewSubmitted}
                                                        isResubmitted={isResubmitted}
                                                        isHead={isHead}
                                                    />
                                                </div>
                                            );
                                        }

                                        if (selFreqForAsset === 'All') {
                                            const freqSet = new Set(allCheckpoints.map(cp => cp.frequency));
                                            const orderedFreqs = [...FREQUENCY_ORDER.filter(f => freqSet.has(f)), ...[...freqSet].filter((f: string) => !FREQUENCY_ORDER.includes(f as (typeof FREQUENCY_ORDER)[number]))];
                                            const groupOrder: CheckpointDisplayGroup[] = ['fromMaster', 'updatedFromMaster', 'newAtProject'];
                                            return (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {orderedFreqs.map((freq) => {
                                                        const list = allCheckpoints.filter(cp => cp.frequency === freq);
                                                        if (list.length === 0) return null;
                                                        const byGroup = groupOrder.map((grp) => ({ group: grp, checkpoints: list.filter((cp) => getCheckpointDisplayGroup(cp) === grp) })).filter((g) => g.checkpoints.length > 0);
                                                        const approved = list.filter(cp => cp.approvalStatus === 'approved').length;
                                                        const freqStyle = FREQUENCY_COLORS[freq] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
                                                        return (
                                                            <div key={freq} className={`rounded-md border p-2 ${freqStyle.border} ${freqStyle.bg}`}>
                                                                <div className="flex items-center justify-between gap-2 mb-1.5 px-0.5">
                                                                    <span className={`text-[9px] font-bold uppercase tracking-wide flex items-center gap-1 ${freqStyle.text}`}>
                                                                        <FileCheck className="w-3 h-3" /> {freq}
                                                                    </span>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-[9px] text-slate-600 tabular-nums">{approved}/{list.length}</span>
                                                                        {isHead && !isReviewSubmitted && selectAllCheckpointsInFrequency && (
                                                                            <button type="button" onClick={() => selectAllCheckpointsInFrequency(asset, freq)} className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold rounded-lg border hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ${freqStyle.text} ${freqStyle.bg} ${freqStyle.border}`}>Select all</button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {byGroup.map(({ group, checkpoints }) => {
                                                                    const style = CHECKPOINT_GROUP_STYLES[group];
                                                                    const SectionIcon = group === 'fromMaster' ? FileCheck : group === 'updatedFromMaster' ? Pencil : PlusCircle;
                                                                    return (
                                                                        <div key={group} className={`rounded-lg border p-2 mt-2 first:mt-0 ${style.sectionBorder} ${style.sectionBg}`}>
                                                                            <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
                                                                                <SectionIcon className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                                                                                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-700">{style.label}</span>
                                                                                <span className="text-[9px] text-slate-500 tabular-nums">({checkpoints.length})</span>
                                                                            </div>
                                                                            <CheckpointTimeline
                                                                                checkpoints={checkpoints}
                                                                                onAction={onCheckpointAction}
                                                                                isSelected={(id) => selectedCheckpoints.has(id)}
                                                                                onToggleSelection={isHead ? toggleCheckpointSelection : undefined}
                                                                                isReviewSubmitted={isReviewSubmitted}
                                                                                isResubmitted={isResubmitted}
                                                                                isHead={isHead}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        }

                                        const freq = selFreqForAsset;
                                        const list = filteredByTab;
                                        const approved = list.filter(cp => cp.approvalStatus === 'approved').length;
                                        const freqStyle = FREQUENCY_COLORS[freq] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
                                        const groupOrder: CheckpointDisplayGroup[] = ['fromMaster', 'updatedFromMaster', 'newAtProject'];
                                        const byGroup = groupOrder.map((grp) => ({ group: grp, checkpoints: list.filter((cp) => getCheckpointDisplayGroup(cp) === grp) })).filter((g) => g.checkpoints.length > 0);
                                        let singleFreqIndex = 0;
                                        return (
                                            <div className={`rounded-md border p-2 ${freqStyle.border} ${freqStyle.bg}`}>
                                                <div className="flex items-center justify-between gap-2 mb-1.5 px-0.5">
                                                    <span className={`text-[9px] font-bold uppercase tracking-wide flex items-center gap-1 ${freqStyle.text}`}>
                                                        <FileCheck className="w-3 h-3" /> {freq}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[9px] text-slate-600 tabular-nums">{approved}/{list.length}</span>
                                                        {isHead && !isReviewSubmitted && selectAllCheckpointsInFrequency && (
                                                            <button type="button" onClick={() => selectAllCheckpointsInFrequency(asset, freq)} className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold rounded-lg border hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ${freqStyle.text} ${freqStyle.bg} ${freqStyle.border}`}>Select all</button>
                                                        )}
                                                    </div>
                                                </div>
                                                {byGroup.map(({ group, checkpoints }) => {
                                                    const style = CHECKPOINT_GROUP_STYLES[group];
                                                    const startIndex = singleFreqIndex;
                                                    singleFreqIndex += checkpoints.length;
                                                    const SectionIcon = group === 'fromMaster' ? FileCheck : group === 'updatedFromMaster' ? Pencil : PlusCircle;
                                                    return (
                                                        <div key={group} className={`rounded-lg border p-2 mt-2 first:mt-0 ${style.sectionBorder} ${style.sectionBg}`}>
                                                            <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
                                                                <SectionIcon className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                                                                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-700">{style.label}</span>
                                                                <span className="text-[9px] text-slate-500 tabular-nums">({checkpoints.length})</span>
                                                            </div>
                                                                            <CheckpointTimeline
                                                                                checkpoints={checkpoints}
                                                                                onAction={onCheckpointAction}
                                                                                isSelected={(id) => selectedCheckpoints.has(id)}
                                                                                onToggleSelection={isHead ? toggleCheckpointSelection : undefined}
                                                                                isReviewSubmitted={isReviewSubmitted}
                                                                                isResubmitted={isResubmitted}
                                                                                isHead={isHead}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        );
                    })}
                </div>
            </div>
        );
    };

// Brand Colors for consistency
export const BRAND_COLORS = {
    primary: "#339274",
    primaryHover: "#29795f",
    primaryLight: "#F8FCFA",
};

export const ResourcesTab: React.FC<{
    resources: ProjectApprovalDetails['resources'];
    projectId: string;
    onResourceApprove: (resource: ResourceApprovalDetail, resourceName: string, resourceType?: 'engineer' | 'supervisor', supervisorId?: string) => void;
    onResourceReject: (resource: ResourceApprovalDetail, resourceName: string, resourceType?: 'engineer' | 'supervisor', supervisorId?: string, engineerIds?: string[]) => void;
    actionLoading: boolean;
    onSyncAssignments: () => void;
    syncLoading: boolean;
    isReviewSubmitted?: boolean;
    isResubmitted?: boolean;
    isHead?: boolean;
}> = (props) => {
    return <ResourceTreeView {...props} />;
};

export const RejectionSummary: React.FC<{
    rejectedItems: {
        checkpoints: CheckpointApprovalDetail[];
        resources: ResourceApprovalDetail[];
    };
    onFix: (type: 'checkpoint' | 'resource', item: any) => void;
}> = ({ rejectedItems, onFix }) => {
    const hasCheckpoints = rejectedItems.checkpoints.length > 0;
    const hasResources = rejectedItems.resources.length > 0;

    if (!hasCheckpoints && !hasResources) {
        return (
            <div className="p-8 text-center">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-4">
                    <CheckCircle className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">No Rejections Found</h3>
                <p className="mt-2 text-sm text-gray-500">All items have been approved or are pending review.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <AlertCircle className="h-5 w-5" />
                </div>
                <div>
                    <p className="font-semibold text-amber-900">Action Required</p>
                    <p className="text-sm mt-1 text-amber-800">The following items have been rejected. Address the feedback and resubmit for review.</p>
                </div>
            </div>

            {hasCheckpoints && (
                <div className="space-y-4">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <FileCheck className="h-5 w-5 text-slate-500" />
                        Rejected Checkpoints ({rejectedItems.checkpoints.length})
                    </h3>
                    <div className="grid gap-3">
                        {rejectedItems.checkpoints.map((cp) => (
                            <div key={cp._id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-slate-300 transition-colors">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900">{cp.name}</p>
                                        <p className="text-xs text-slate-500 mt-1">Frequency: {cp.frequency}</p>
                                        <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                                            <span className="text-xs font-semibold text-red-800">Rejection reason:</span>
                                            <p className="text-xs text-red-700 mt-1">{cp.approverComments}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => onFix('checkpoint', cp)}
                                        className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                                    >
                                        <Package className="h-4 w-4" />
                                        Go to Project Assets
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {hasResources && (
                <div className="space-y-4">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <Users className="h-5 w-5 text-slate-500" />
                        Rejected Resources ({rejectedItems.resources.length})
                    </h3>
                    <div className="grid gap-3">
                        {rejectedItems.resources.map((res) => (
                            <div key={res._id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-slate-300 transition-colors">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-medium text-gray-900">{res.name}</p>
                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded-full uppercase font-medium">
                                                {res.role.name}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">{res.email}</p>
                                        <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                                            <span className="text-xs font-semibold text-red-800">Rejection reason:</span>
                                            <p className="text-xs text-red-700 mt-1">{res.approverComments}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => onFix('resource', res)}
                                        className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                                    >
                                        <Users className="h-4 w-4" />
                                        Go to Assignments
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
